<template>
  <div class="bcb-schedule-day">
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h6 class="font-weight-bold mb-0">Danh sách ngày khám</h6>
      <button v-if="isInsertPermission" class="btn btn-success btn-sm" @click="openAddDayModal">
        <i class="fas fa-plus mr-2"></i>Thêm ngày
      </button>
    </div>

    <b-overlay :show="loading">
      <div v-if="days.length === 0 && !loading" class="text-center text-muted py-8">
        <i class="fas fa-calendar-times fa-3x mb-3 d-block"></i>
        Chưa có ngày khám nào. Thêm ngày để bắt đầu.
      </div>

      <div v-for="day in days" :key="day.id" class="day-card mb-4">
        <div class="day-card__header" @click="toggleDay(day)" style="cursor: pointer;">
          <div class="d-flex align-items-center">
            <i class="fas fa-calendar-day mr-3 text-primary"></i>
            <div>
              <div class="font-weight-bold">{{ formatDate(day.examDate) }}</div>
              <small class="text-muted">Tối đa {{ day.maxPerDay }} người / ngày</small>
            </div>
          </div>
          <div class="d-flex align-items-center">
            <button v-if="isInsertPermission" class="btn btn-outline-success btn-sm mr-2" @click.stop="openAddSlotModal(day)">
              <i class="fas fa-plus mr-1"></i>Thêm slot
            </button>
            <button v-if="isInsertPermission" class="btn btn-outline-primary btn-sm mr-2" title="Nhân bản ngày khám" @click.stop="openDuplicateDayModal(day)">
              <i class="fas fa-copy"></i>
            </button>
            <button v-if="isUpdatePermission" class="btn btn-outline-warning btn-sm mr-2" @click.stop="openEditDayModal(day)">
              <i class="fas fa-edit"></i>
            </button>
            <button v-if="isDeletePermission" class="btn btn-outline-danger btn-sm mr-2" @click.stop="deleteDay(day)">
              <i class="fas fa-trash"></i>
            </button>
            <b-spinner v-if="loadingSlots[day.id]" small class="mr-2" />
            <i v-else :class="expandedDays[day.id] ? 'fas fa-chevron-up' : 'fas fa-chevron-down'" class="text-muted"></i>
          </div>
        </div>

        <!-- Slots (lazy load on click) -->
        <div v-show="expandedDays[day.id]" class="day-card__slots mt-3">
          <div v-if="!day.slots || day.slots.length === 0" class="text-muted text-sm pl-4">
            Chưa có slot nào.
          </div>
          <div v-for="slot in day.slots" :key="slot.id" class="slot-row">
            <div class="d-flex align-items-center">
              <i class="fas fa-clock mr-2 text-info"></i>
              <span class="font-weight-medium">{{ formatTime(slot.startTime) }} — {{ formatTime(slot.endTime) }}</span>
              <span class="mx-3 text-muted">|</span>
              <span class="text-muted">Sức chứa: {{ slot.maxCapacity }}</span>
            </div>
            <div>
              <button v-if="isUpdatePermission" class="btn btn-icon btn-light btn-sm mr-1" @click="openEditSlotModal(slot, day)">
                <i class="fas fa-edit text-warning"></i>
              </button>
              <button v-if="isDeletePermission" class="btn btn-icon btn-light btn-sm" @click="deleteSlot(slot)">
                <i class="fas fa-trash text-danger"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </b-overlay>

    <!-- Modal Thêm/Sửa ngày -->
    <b-modal
      v-model="showDayModal"
      :title="editingDay ? 'Chỉnh sửa ngày khám' : 'Thêm ngày khám'"
      ok-title="Lưu"
      cancel-title="Huỷ"
      ok-variant="success"
      @ok="saveDay"
    >
      <div>
        <BasicDatePicker
          label="Ngày khám (*)"
          placeholder="Chọn ngày"
          name="scheduleDate"
          format="YYYY-MM-DD"
          value-type="format"
          :value.sync="dayForm.scheduleDateStr"
        />
        <basic-input
          label="Tối đa người / ngày (*)"
          placeholder="Nhập số lượng tối đa"
          name="maxPerDay"
          type="number"
          :value.sync="dayForm.maxPerDay"
        />
      </div>
    </b-modal>

    <!-- Modal Nhân bản ngày khám -->
    <b-modal
      v-model="showDuplicateModal"
      title="Nhân bản ngày khám"
      ok-title="Nhân bản"
      cancel-title="Huỷ"
      ok-variant="success"
      @ok="duplicateDay"
    >
      <div>
        <p class="text-muted mb-4">
          Sao chép toàn bộ khung giờ của ngày
          <strong>{{ formatDate(duplicateSourceDay && duplicateSourceDay.examDate) }}</strong>
          sang một ngày khác.
        </p>
        <BasicDatePicker
          label="Ngày khám mới (*)"
          placeholder="Chọn ngày muốn nhân bản sang"
          name="duplicateScheduleDate"
          format="YYYY-MM-DD"
          value-type="format"
          :value.sync="duplicateForm.scheduleDateStr"
        />
      </div>
    </b-modal>

    <b-modal
      v-model="showSlotModal"
      :title="editingSlot ? 'Chỉnh sửa slot' : 'Thêm slot giờ'"
      ok-title="Lưu"
      cancel-title="Huỷ"
      ok-variant="success"
      @ok="saveSlot"
    >
      <div>
        <BasicDatePicker
          label="Giờ bắt đầu (*)"
          placeholder="VD: 08:00"
          name="startTime"
          type="time"
          format="HH:mm"
          value-type="format"
          :value.sync="slotForm.startTime"
        />
        <BasicDatePicker
          label="Giờ kết thúc (*)"
          placeholder="VD: 09:00"
          name="endTime"
          type="time"
          format="HH:mm"
          value-type="format"
          :value.sync="slotForm.endTime"
        />
        <basic-input
          label="Sức chứa tối đa (*)"
          placeholder="Nhập số lượng"
          name="maxCapacity"
          type="number"
          :value.sync="slotForm.maxCapacity"
        />
      </div>
    </b-modal>
  </div>
</template>

<script>
export default {
  name: 'BcbScheduleDayManagement',
  props: {
    partnerId: { type: [String, Number], required: true },
  },
  data() {
    return {
      loading: false,
      days: [],
      expandedDays: {},
      loadingSlots: {},
      showDayModal: false,
      showSlotModal: false,
      showDuplicateModal: false,
      editingDay: null,
      editingSlot: null,
      currentDay: null,
      duplicateSourceDay: null,
      dayForm: { scheduleDateStr: '', maxPerDay: '' },
      slotForm: { startTime: '', endTime: '', maxCapacity: '' },
      duplicateForm: { scheduleDateStr: '' },
    };
  },
  created() {
    this.loadDays();
  },
  methods: {
    // Luôn gọi API lấy lại slot của 1 ngày (dùng cho cả lazy load lẫn reload sau khi thêm/xoá slot)
    async fetchSlots(day) {
      this.$set(this.loadingSlots, day.id, true);
      try {
        const sRes = await this.$api.get('BcbPartnerScheduleSlot', {
          params: { scheduleDayId: day.id },
        });
        const sRaw = sRes?.data ?? sRes;
        this.$set(day, 'slots', sRaw?.items ?? sRaw?.data?.items ?? (Array.isArray(sRaw) ? sRaw : []));
      } catch {
        this.$set(day, 'slots', []);
      } finally {
        this.$set(this.loadingSlots, day.id, false);
      }
    },
    async toggleDay(day) {
      const isExpanded = this.expandedDays[day.id];
      if (isExpanded) {
        this.$set(this.expandedDays, day.id, false);
        return;
      }
      // Lazy load slots nếu chưa có
      if (!day.slots) {
        await this.fetchSlots(day);
      }
      this.$set(this.expandedDays, day.id, true);
    },
    async loadDays() {
      this.loading = true;
      try {
        const res = await this.$api.get('BcbPartnerScheduleDay', {
          params: { partnerId: this.partnerId, page: 1, size: 1000 },
        });
        const raw = res?.data ?? res;
        const list = raw?.items ?? raw?.data?.items ?? (Array.isArray(raw) ? raw : []);
        this.days = list;
        this.expandedDays = {};
        this.loadingSlots = {};
      } catch (e) {
        this.$toastr.e({ title: 'Lỗi', msg: 'Không thể tải lịch khám.' });
      } finally {
        this.loading = false;
      }
    },
    openAddDayModal() {
      this.editingDay = null;
      this.dayForm = { scheduleDateStr: '', maxPerDay: '' };
      this.showDayModal = true;
    },
    openEditDayModal(day) {
      this.editingDay = day;
      this.dayForm = {
        scheduleDateStr: this.$moment.unix(day.examDate).utcOffset('+07:00').format('YYYY-MM-DD'),
        maxPerDay: day.maxPerDay,
      };
      this.showDayModal = true;
    },
    openDuplicateDayModal(day) {
      this.duplicateSourceDay = day;
      this.duplicateForm = { scheduleDateStr: '' };
      this.showDuplicateModal = true;
    },
    async duplicateDay(bvModalEvent) {
      if (!this.duplicateForm.scheduleDateStr) {
        if (bvModalEvent && bvModalEvent.preventDefault) bvModalEvent.preventDefault();
        this.$toastr.e({ title: 'Lỗi', msg: 'Vui lòng chọn ngày muốn nhân bản sang.' });
        return;
      }
      const formData = this.buildFormData({
        examDate:
          this.convertDateToTimestamp(this.duplicateForm.scheduleDateStr, 'YYYY-MM-DD') + 7 * 3600,
      });
      try {
        await this.$api.post(
          `BcbPartnerScheduleDay/${this.duplicateSourceDay.id}/duplicate`,
          formData,
        );
        this.$toastr.s({ title: 'Thành công', msg: 'Đã nhân bản ngày khám.' });
        this.loadDays();
      } catch (e) {
        this.$toastr.e({ title: 'Lỗi', msg: e?.message || 'Không thể nhân bản ngày khám.' });
      }
    },
    buildFormData(obj) {
      const fd = new FormData();
      Object.entries(obj).forEach(([key, val]) => {
        if (val !== null && val !== undefined) fd.append(key, val);
      });
      return fd;
    },
    async saveDay() {
      const formData = this.buildFormData({
        partnerId: this.partnerId,
        examDate: this.convertDateToTimestamp(this.dayForm.scheduleDateStr, 'YYYY-MM-DD') + 7 * 3600,
        maxPerDay: Number(this.dayForm.maxPerDay),
        ...(this.editingDay && {
          campaignId: this.editingDay.campaignId,
          bookedCount: this.editingDay.bookedCount ?? 0,
          isActive: true,
        }),
      });
      try {
        if (this.editingDay) {
          await this.$api.put(`BcbPartnerScheduleDay/${this.editingDay.id}`, formData);
          this.$toastr.s({ title: 'Thành công', msg: 'Đã cập nhật ngày khám.' });
        } else {
          await this.$api.post('BcbPartnerScheduleDay', formData);
          this.$toastr.s({ title: 'Thành công', msg: 'Đã thêm ngày khám.' });
        }
        this.loadDays();
      } catch (e) {
        this.$toastr.e({ title: 'Lỗi', msg: e?.message || 'Không thể lưu ngày khám.' });
      }
    },
    async deleteDay(day) {
      const confirm = await this.$swal({
        title: 'Xác nhận xoá',
        text: `Xoá ngày khám ${this.formatDate(day.examDate)} và tất cả slot?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Xoá',
        cancelButtonText: 'Huỷ',
      });
      if (!(confirm === true || confirm?.value || confirm?.isConfirmed)) return;
      try {
        await this.$api.delete(`/BcbPartnerScheduleDay/${day.id}/with-slots`);
        this.$toastr.s({ title: 'Thành công', msg: 'Đã xoá ngày khám.' });
        this.loadDays();
      } catch (e) {
        this.$toastr.e({ title: 'Lỗi', msg: e?.message || 'Không thể xoá.' });
      }
    },
    openAddSlotModal(day) {
      this.currentDay = day;
      this.editingSlot = null;
      this.slotForm = { startTime: '', endTime: '', maxCapacity: '' };
      this.showSlotModal = true;
    },
    openEditSlotModal(slot, day) {
      this.currentDay = day;
      this.editingSlot = slot;
      this.slotForm = {
        startTime: this.formatTime(slot.startTime),
        endTime: this.formatTime(slot.endTime),
        maxCapacity: slot.maxCapacity,
      };
      this.showSlotModal = true;
    },
    timeToMinutes(value) {
      const parts = String(value || '').split(':');
      if (parts.length < 2) return null;
      const hours = Number(parts[0]);
      const minutes = Number(parts[1]);
      if (Number.isNaN(hours) || Number.isNaN(minutes)) return null;
      return hours * 60 + minutes;
    },
    validateSlotForm() {
      const startMinutes = this.timeToMinutes(this.slotForm.startTime);
      const endMinutes = this.timeToMinutes(this.slotForm.endTime);
      const maxCapacity = Number(this.slotForm.maxCapacity);

      if (startMinutes === null || endMinutes === null) {
        this.$toastr.e({ title: 'Lỗi', msg: 'Vui lòng chọn giờ bắt đầu và giờ kết thúc.' });
        return false;
      }

      if (endMinutes <= startMinutes) {
        this.$toastr.e({ title: 'Lỗi', msg: 'Giờ kết thúc phải lớn hơn giờ bắt đầu.' });
        return false;
      }

      if (!this.slotForm.maxCapacity || Number.isNaN(maxCapacity) || maxCapacity <= 0) {
        this.$toastr.e({ title: 'Lỗi', msg: 'Sức chứa tối đa là bắt buộc và phải lớn hơn 0.' });
        return false;
      }

      return true;
    },
    validateDayCapacity() {
      if (!this.currentDay) return true;
      const maxCapacity = Number(this.slotForm.maxCapacity);
      const currentTotal = (this.currentDay.slots || [])
        .filter((slot) => !this.editingSlot || slot.id !== this.editingSlot.id)
        .reduce((total, slot) => total + Number(slot.maxCapacity || 0), 0);

      if (currentTotal + maxCapacity > Number(this.currentDay.maxPerDay || 0)) {
        this.$toastr.e({ title: 'Lỗi', msg: 'Số lượng không đủ.' });
        return false;
      }

      return true;
    },
    async saveSlot(bvModalEvent) {
      if (!this.validateSlotForm() || !this.validateDayCapacity()) {
        if (bvModalEvent && bvModalEvent.preventDefault) bvModalEvent.preventDefault();
        return;
      }

      const formData = this.buildFormData({
        scheduleDayId: this.currentDay.id,
        startTime: this.slotForm.startTime,
        endTime: this.slotForm.endTime,
        maxCapacity: Number(this.slotForm.maxCapacity),
      });
      try {
        if (this.editingSlot) {
          await this.$api.put(`BcbPartnerScheduleSlot/${this.editingSlot.id}`, formData);
          this.$toastr.s({ title: 'Thành công', msg: 'Đã cập nhật slot.' });
        } else {
          await this.$api.post('BcbPartnerScheduleSlot', formData);
          this.$toastr.s({ title: 'Thành công', msg: 'Đã thêm slot.' });
        }
        // Reload slots của ngày hiện tại và giữ ngày ở trạng thái mở
        await this.fetchSlots(this.currentDay);
        this.$set(this.expandedDays, this.currentDay.id, true);
      } catch (e) {
        this.$toastr.e({ title: 'Lỗi', msg: e?.message || 'Không thể lưu slot.' });
      }
    },
    async deleteSlot(slot) {
      const confirm = await this.$swal({
        title: 'Xác nhận xoá',
        text: `Xoá slot ${this.formatTime(slot.startTime)} - ${this.formatTime(slot.endTime)}?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Xoá',
        cancelButtonText: 'Huỷ',
      });
      if (!(confirm === true || confirm?.value || confirm?.isConfirmed)) return;
      try {
        await this.$api.delete(`/BcbPartnerScheduleSlot/${slot.id}`);
        this.$toastr.s({ title: 'Thành công', msg: 'Đã xoá slot.' });
        // Tìm ngày chứa slot vừa xoá (không phụ thuộc currentDay - biến này chỉ được gán khi mở modal)
        const day =
          this.days.find((d) => d.id === slot.scheduleDayId) ||
          this.days.find((d) => d.slots?.some((s) => s.id === slot.id));
        if (day) {
          await this.fetchSlots(day);
          this.$set(this.expandedDays, day.id, true);
        }
      } catch (e) {
        this.$toastr.e({ title: 'Lỗi', msg: e?.message || 'Không thể xoá.' });
      }
    },
    formatDate(val) {
      if (!val) return '—';
      return this.$moment.unix(val).utcOffset('+07:00').format('DD/MM/YYYY');
    },
    /**
     * Parse TimeSpan từ .NET (VD: "08:00:00", "8.00:00:00") → "HH:mm"
     * C# TimeSpan JSON: "HH:mm:ss" (< 1 ngày) hoặc "d.HH:mm:ss" (>= 1 ngày)
     */
    formatTime(val) {
      if (!val) return '—';
      // Bỏ phần "d." nếu có (VD: "8.00:00:00" → "00:00:00")
      const timePart = String(val).includes('.') ? String(val).split('.').pop() : String(val);
      // Lấy HH:mm (2 phần đầu)
      const parts = timePart.split(':');
      if (parts.length >= 2) {
        return `${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}`;
      }
      return val;
    },
  },
};
</script>

<style scoped lang="scss">
.day-card {
  border: 1px solid #e4e6ef;
  border-radius: 12px;
  overflow: hidden;

  &__header {
    background: #f8f9fa;
    padding: 14px 18px;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  &__slots {
    padding: 0 18px 14px;
  }
}

.slot-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #f3f4f8;
  font-size: 0.88rem;

  &:last-child { border-bottom: none; }
}
</style>
